import { useState, useEffect } from 'react';
import { BlockingDialog, BlockingDialogContent, BlockingDialogHeader, BlockingDialogTitle, BlockingDialogDescription } from '@/components/ui/blocking-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, Loader2, Copy, CreditCard, QrCode, Gift } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackPurchase } from '@/lib/unified-tracking';

interface PaywallModalProps {
  open: boolean;
  productType: 'mascot' | 'brand_kit';
  previewImageUrl?: string;
  onPaymentSuccess: () => void;
}

export const PaywallModal = ({ 
  open, 
  productType,
  previewImageUrl,
  onPaymentSuccess 
}: PaywallModalProps) => {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [polling, setPolling] = useState(false);

  const products = {
    mascot: {
      name: 'Mascote Exclusivo',
      price: 'R$ 29,90',
      originalPrice: 'R$ 89,90',
      discount: '67% OFF - Lançamento',
      features: [
        '3 variações de mascote geradas por IA',
        'Versões com e sem fundo (PNG)',
        'Logo da marca integrado ao uniforme',
        'Alta resolução para impressão',
        'Uso comercial liberado'
      ]
    },
    brand_kit: {
      name: 'Kit Marca Completo',
      price: 'R$ 89,90',
      originalPrice: 'R$ 299,90',
      discount: '70% OFF - Lançamento',
      features: [
        'Até 10 versões da sua marca',
        'PNG com fundo transparente',
        'Paleta de 4 cores (códigos HEX)',
        '3 tipografias Fontshare gratuitas',
        'Uso comercial liberado'
      ],
      bonus: '🎁 BÔNUS: 1 Mascote Exclusivo grátis!'
    }
  };

  const product = products[productType];

  // Anti-DevTools protection
  useEffect(() => {
    if (!open) return;

    // Detect if paywall element is removed from DOM
    const checkPaywall = setInterval(() => {
      const paywallElement = document.querySelector('[data-paywall="true"]');
      if (!paywallElement && open) {
        // Force page reload if paywall was manipulated
        window.location.reload();
      }
    }, 500);

    // Block DevTools shortcuts
    const blockDevTools = (e: KeyboardEvent) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C' || e.key === 'J' || e.key === 'i' || e.key === 'c' || e.key === 'j')) ||
        (e.ctrlKey && e.key === 'u') || // View source
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Disable right-click context menu when paywall is open
    const blockContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('keydown', blockDevTools, true);
    document.addEventListener('contextmenu', blockContextMenu, true);

    return () => {
      clearInterval(checkPaywall);
      document.removeEventListener('keydown', blockDevTools, true);
      document.removeEventListener('contextmenu', blockContextMenu, true);
    };
  }, [open]);

  useEffect(() => {
    const loadUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setEmail(user.email);
      }
    };
    loadUserData();
  }, []);


  // Poll for payment status
  useEffect(() => {
    if (!paymentData?.order_id || !polling) return;

    const pollInterval = setInterval(async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: purchase } = await supabase
          .from('purchases')
          .select('*')
          .eq('user_id', user.id)
          .eq('product_type', productType)
          .single();

        if (purchase) {
          setPolling(false);
          
          // Track Purchase (Meta + TikTok)
          trackPurchase({
            value: productType === 'brand_kit' ? 89.90 : 29.90,
            productName: product.name,
          });
          
          toast.success('Pagamento confirmado! 🎉');
          onPaymentSuccess();
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [paymentData, polling, productType, onPaymentSuccess]);

  const handlePayment = async (method: 'pix' | 'card') => {
    if (!email || !name) {
      toast.error('Preencha seu nome e email para continuar');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          productType,
          payerEmail: email,
          payerName: name,
          paymentMethod: method,
        }
      });

      if (error) throw error;

      if (data.success) {
        if (method === 'card' && data.checkout_url) {
          
          // Redirect to Mercado Pago Checkout
          window.location.href = data.checkout_url;
        } else {
          
          // PIX payment - show QR code
          setPaymentData(data);
          setPolling(true);
          toast.success('Pagamento PIX criado! Use o QR Code ou código');
        }
      } else {
        throw new Error(data.error || 'Failed to create payment');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Erro ao criar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const copyPixCode = () => {
    if (paymentData?.qr_code) {
      navigator.clipboard.writeText(paymentData.qr_code);
      toast.success('Código copiado!');
    }
  };

  return (
    <BlockingDialog open={open} modal={true}>
      <BlockingDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <BlockingDialogHeader>
          <div className="inline-block bg-destructive text-destructive-foreground px-3 py-1 rounded-full text-sm font-semibold mb-3 w-fit">
            🚀 CYBER WEEK {product.discount}
          </div>
          <BlockingDialogTitle className="text-2xl">{product.name}</BlockingDialogTitle>
          <BlockingDialogDescription className="text-base">
            {productType === 'brand_kit' 
              ? 'Após o pagamento, você poderá gerar até 10 versões da sua marca'
              : 'Gere até 3 variações do mascote após o pagamento'}
          </BlockingDialogDescription>
        </BlockingDialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          {/* Preview + Product Details */}
          <div className="space-y-4">
            {previewImageUrl && (
              <div className="relative rounded-lg overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 p-4">
                {/* Server-generated blurred preview - no CSS blur needed */}
                <img 
                  src={previewImageUrl} 
                  alt="Preview" 
                  className="w-full h-48 object-contain"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/60 text-white px-4 py-2 rounded-lg text-sm font-medium">
                    ✨ Pronto para usar. Desbloqueie agora.
                  </div>
                </div>
              </div>
            )}

            <Card className="p-4">
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-bold">{product.price}</span>
                <span className="text-lg text-muted-foreground line-through">{product.originalPrice}</span>
              </div>

              <div className="space-y-2">
                <p className="font-semibold text-sm mb-2">✨ O que está incluído:</p>
                {product.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
                {'bonus' in product && product.bonus && (
                  <div className="flex items-start gap-2 mt-3 p-2 bg-accent/10 rounded-lg">
                    <Gift className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-accent">{product.bonus}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Payment Form */}
          <div>
            {!paymentData ? (
              <Card className="p-6">
                <h3 className="text-lg font-bold mb-4">Finalizar Compra</h3>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="name">Nome Completo</Label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                      }}
                      placeholder="Seu nome completo"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                      placeholder="seu@email.com"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => handlePayment('card')}
                    disabled={loading}
                    className="w-full"
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Desbloquear com Cartão
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={() => handlePayment('pix')}
                    disabled={loading}
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <QrCode className="w-5 h-5 mr-2" />
                        Desbloquear com PIX
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground mt-4 text-center">
                  Pagamento 100% seguro via Mercado Pago
                </p>
              </Card>
            ) : (
              <Card className="p-6">
                <h3 className="text-lg font-bold mb-4">Pagar com PIX</h3>
                
                {paymentData.qr_code_base64 && (
                  <div className="bg-white p-4 rounded-lg mb-4 flex justify-center">
                    <img 
                      src={`data:image/png;base64,${paymentData.qr_code_base64}`}
                      alt="QR Code PIX"
                      className="w-48 h-48"
                    />
                  </div>
                )}

                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Escaneie o QR Code ou copie o código PIX:
                  </p>

                  <div className="flex gap-2">
                    <Input
                      value={paymentData.qr_code || ''}
                      readOnly
                      className="font-mono text-xs"
                    />
                    <Button onClick={copyPixCode} variant="outline" size="icon">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="bg-muted p-4 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <p className="text-sm font-semibold">Aguardando pagamento</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Assim que o pagamento for confirmado, você terá acesso imediato às gerações extras!
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </BlockingDialogContent>
    </BlockingDialog>
  );
};
